/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Row, Col, Select, Table, Tag, Tooltip, Spin, Alert, message, Statistic
} from 'antd';
import { LoadingOutlined, FileImageOutlined, FilePdfOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';

// ─── Constants ──────────────────────────────────────────────────────────────
const SECTION_COLORS = {
    CC1:'#0ea5e9', CC2:'#8b5cf6', CC3:'#f59e0b', CC4:'#10b981',
    CC5:'#ef4444', CC6:'#3b82f6', CC7:'#6366f1', CC8:'#ec4899',
};
const SECTION_TITLES = {
    CC1:'IPC Programme', CC2:'IPC Guidelines', CC3:'Education & Training', CC4:'HAI Surveillance',
    CC5:'Multimodal Strategies', CC6:'Monitoring & Audit', CC7:'Workload & Staffing', CC8:'Built Environment',
};
const LEVEL_COLORS = { Inadequate:'#f5222d', Basic:'#fa8c16', Intermediate:'#1890ff', Advanced:'#389e0d' };

// ─── XCard: card wrapper with PNG/PDF export, mirroring QoCExecutiveDashboard's pattern ──
function useNodeExport(nodeRef) {
    const [busy, setBusy] = useState(null);

    const capture = async (el, scale) => {
        const { default: h2c } = await import('html2canvas');
        const hidden = [];
        el.querySelectorAll('[data-xcard-btns]').forEach(n => {
            n._v = n.style.visibility; n.style.visibility = 'hidden'; hidden.push(n);
        });
        const origOverflow = el.style.overflow;
        el.style.overflow = 'visible';
        const rect   = el.getBoundingClientRect();
        const scrollW = Math.max(el.scrollWidth, Math.ceil(rect.width));
        const scrollH = Math.max(el.scrollHeight, Math.ceil(rect.height));
        const canvas = await h2c(el, {
            scale, useCORS: true, allowTaint: true, backgroundColor: '#ffffff',
            logging: false, width: scrollW, height: scrollH,
            windowWidth: scrollW, windowHeight: scrollH, scrollX: 0, scrollY: 0,
            onclone: (clonedDoc) => {
                const origs   = el.querySelectorAll('canvas');
                const cloneds = clonedDoc.querySelectorAll('canvas');
                origs.forEach((orig, i) => {
                    const cl = cloneds[i];
                    if (cl && orig.width > 0 && orig.height > 0) {
                        try { cl.width = orig.width; cl.height = orig.height; cl.getContext('2d').drawImage(orig, 0, 0); }
                        catch (e) { /* skip cross-origin */ }
                    }
                });
            },
        });
        el.style.overflow = origOverflow;
        hidden.forEach(n => { n.style.visibility = n._v || ''; delete n._v; });
        return canvas;
    };

    const exportPNG = async (filename) => {
        if (!nodeRef.current) return;
        setBusy('png');
        try {
            const canvas = await capture(nodeRef.current, 2);
            const a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = `${filename}.png`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
        } catch (e) { message.error('PNG export failed: ' + e.message); }
        finally { setBusy(null); }
    };

    const exportPDF = async (filename, label) => {
        if (!nodeRef.current) return;
        setBusy('pdf');
        try {
            const canvas = await capture(nodeRef.current, 2);
            const { jsPDF } = await import('jspdf');
            const imgData = canvas.toDataURL('image/png');
            const pxToMm = px => px * 0.264583;
            const w = pxToMm(canvas.width), h = pxToMm(canvas.height);
            const pdf = new jsPDF({ orientation: w > h ? 'landscape' : 'portrait', unit: 'mm', format: [w, h] });
            pdf.addImage(imgData, 'PNG', 0, 0, w, h);
            pdf.save(`${filename}.pdf`);
        } catch (e) { message.error('PDF export failed: ' + e.message); }
        finally { setBusy(null); }
    };

    return { busy, exportPNG, exportPDF };
}

function XCard({ title, filename, children, extra, style, bodyStyle, size = 'small' }) {
    const ref  = useRef(null);
    const safe = (filename || (typeof title === 'string' ? title : 'chart')).replace(/[^a-zA-Z0-9_-]/g, '_');
    const label = typeof title === 'string' ? title : safe;
    const { busy, exportPNG, exportPDF } = useNodeExport(ref);

    const btns = (
        <span data-xcard-btns style={{ display:'inline-flex', gap:4, marginLeft:8 }}>
            <Tooltip title="Save as PNG">
                <span onClick={() => exportPNG(safe)}
                    style={{ cursor: busy === 'png' ? 'wait' : 'pointer',
                        display:'inline-flex', alignItems:'center', gap:3,
                        padding:'2px 7px', borderRadius:4, fontSize:11,
                        border:'1px solid #d9d9d9', color:'#555', background:'#fafafa',
                        opacity: busy && busy !== 'png' ? 0.4 : 1 }}>
                    {busy === 'png' ? <LoadingOutlined style={{ fontSize:10 }}/> : <FileImageOutlined style={{ fontSize:10 }}/>}
                    PNG
                </span>
            </Tooltip>
            <Tooltip title="Save as PDF">
                <span onClick={() => exportPDF(safe, label)}
                    style={{ cursor: busy === 'pdf' ? 'wait' : 'pointer',
                        display:'inline-flex', alignItems:'center', gap:3,
                        padding:'2px 7px', borderRadius:4, fontSize:11,
                        border:'1px solid #ffccc7', color:'#cf1322', background:'#fff2f0',
                        opacity: busy && busy !== 'pdf' ? 0.4 : 1 }}>
                    {busy === 'pdf' ? <LoadingOutlined style={{ fontSize:10 }}/> : <FilePdfOutlined style={{ fontSize:10 }}/>}
                    PDF
                </span>
            </Tooltip>
        </span>
    );

    return (
        <div ref={ref} style={style}>
            <Card size={size} bodyStyle={bodyStyle}
                title={typeof title === 'string' ? <span>{title}{btns}</span> : title}
                extra={extra || (typeof title !== 'string' ? btns : undefined)}>
                {children}
            </Card>
        </div>
    );
}

// ─── Main page ──────────────────────────────────────────────────────────────
export default function QoCIpcafAnalytics() {
    const [loading, setLoading]           = useState(true);
    const [summary, setSummary]           = useState(null);
    const [weakest, setWeakest]           = useState([]);
    const [facilities, setFacilities]     = useState([]);
    const [facilityId, setFacilityId]     = useState(null);
    const [scorecard, setScorecard]       = useState(null);
    const [scorecardLoading, setScorecardLoading] = useState(false);

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data || []));
        Promise.all([
            QoCService.getIpcafNationalSummary(),
            QoCService.getIpcafWeakestIndicators(),
        ]).then(([sumRes, weakRes]) => {
            setSummary(sumRes.data?.data || null);
            setWeakest(weakRes.data?.data || []);
        }).catch(() => message.error('Failed to load IPCAF analytics'))
          .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!facilityId) { setScorecard(null); return; }
        setScorecardLoading(true);
        QoCService.getIpcafFacilityScorecard(facilityId)
            .then(r => setScorecard(r.data?.data || null))
            .catch(() => message.error('Failed to load facility scorecard'))
            .finally(() => setScorecardLoading(false));
    }, [facilityId]);

    const selectedFacility = facilities.find(f => f.id === facilityId);

    // ── IPC level distribution donut ────────────────────────────────────────
    const levelDonutOpt = summary ? {
        tooltip: { trigger:'item' },
        legend: { bottom:0, textStyle:{ fontSize:11 } },
        series: [{
            type:'pie', radius:['45%','70%'],
            data: Object.entries(summary.level_distribution || {}).map(([level, count]) => ({
                name: level, value: count, itemStyle:{ color: LEVEL_COLORS[level] }
            })),
            label: { formatter:'{b}: {c}', fontSize:11 },
        }],
    } : null;

    // ── Region average bar chart ────────────────────────────────────────────
    const regionBarOpt = summary && summary.region_averages?.length ? {
        tooltip: { trigger:'axis' },
        grid: { left:60, right:20, top:20, bottom:30 },
        xAxis: { type:'value', max:800, name:'Avg Score' },
        yAxis: { type:'category', data: summary.region_averages.map(r => r.region) },
        series: [{
            type:'bar', data: summary.region_averages.map(r => r.avg_score),
            itemStyle:{ color:'#1890ff' }, label:{ show:true, position:'right', formatter:'{c}' },
        }],
    } : null;

    // ── Weakest indicators — bottom 15 by pct ───────────────────────────────
    const weakestWithData = weakest.filter(w => w.n_assessments > 0);
    const bottom15 = weakestWithData.slice(0, 15);
    const weakestBarOpt = bottom15.length ? {
        tooltip: { trigger:'axis', formatter: p => {
            const d = bottom15[p[0].dataIndex];
            return `${d.ref_no} (${d.section_code})<br/>${d.question_text}<br/><b>${d.pct}%</b> (avg ${d.avg_score}/${d.max_possible}, n=${d.n_assessments})`;
        }},
        grid: { left:70, right:30, top:10, bottom:10 },
        xAxis: { type:'value', max:100, name:'% of max score' },
        yAxis: { type:'category', inverse:true,
            data: bottom15.map(w => w.ref_no),
            axisLabel: { fontSize:11 } },
        series: [{
            type:'bar', data: bottom15.map(w => ({
                value: w.pct, itemStyle:{ color: SECTION_COLORS[w.section_code] || '#999' }
            })),
            label:{ show:true, position:'right', formatter:'{c}%' },
        }],
    } : null;

    const weakestColumns = [
        { title:'Ref', dataIndex:'ref_no', width:70 },
        { title:'Section', dataIndex:'section_code', width:70,
            render:v => <Tag color={SECTION_COLORS[v]}>{v}</Tag> },
        { title:'Question', dataIndex:'question_text' },
        { title:'Avg Score', width:110, render:(_, r) => `${r.avg_score} / ${r.max_possible}` },
        { title:'% of Max', dataIndex:'pct', width:100, sorter:(a,b) => (a.pct||0)-(b.pct||0),
            render:v => v === null ? '—' : `${v}%` },
        { title:'n', dataIndex:'n_assessments', width:60 },
    ];

    // ── Facility scorecard — radar + trend ──────────────────────────────────
    const radarOpt = scorecard?.latest ? {
        tooltip:{},
        radar: {
            indicator: Object.keys(scorecard.latest.section_scores).map(code => ({
                name: `${code}\n(${SECTION_TITLES[code]})`, max:100,
            })),
            radius:'65%',
        },
        series: [{
            type:'radar',
            data: [{
                value: Object.values(scorecard.latest.section_scores),
                name: 'Section Scores',
                areaStyle:{ color:'rgba(24,144,255,0.2)' },
                lineStyle:{ color:'#1890ff' },
                itemStyle:{ color:'#1890ff' },
            }],
        }],
    } : null;

    const trendOpt = scorecard?.history?.length ? {
        tooltip:{ trigger:'axis' },
        grid:{ left:50, right:20, top:20, bottom:40 },
        xAxis:{ type:'category', data: scorecard.history.map(h => h.assessment_date),
            axisLabel:{ rotate:30, fontSize:10 } },
        yAxis:{ type:'value', max:800, name:'Total Score' },
        series:[{
            type:'line', data: scorecard.history.map(h => h.total_score),
            smooth:true, symbol:'circle', symbolSize:8,
            lineStyle:{ color:'#1890ff', width:3 },
            itemStyle:{ color:'#1890ff' },
            markLine: { silent:true, symbol:'none',
                lineStyle:{ type:'dashed' },
                data:[
                    { yAxis:200, label:{ formatter:'Inadequate/Basic' } },
                    { yAxis:400, label:{ formatter:'Basic/Intermediate' } },
                    { yAxis:600, label:{ formatter:'Intermediate/Advanced' } },
                ],
            },
        }],
    } : null;

    if (loading) return <div style={{ textAlign:'center', padding:80 }}><Spin size="large"/></div>;

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>IPCAF Analytics</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        National IPC performance based on submitted WHO IPCAF assessments
                    </div>
                </div>
            </div>

            <div className="page-pad">
                {/* ── National summary ── */}
                <Row gutter={16} style={{ marginBottom:16 }}>
                    <Col span={5}>
                        <Card size="small">
                            <Statistic title="Facilities Assessed" value={summary?.facilities_assessed || 0}/>
                        </Card>
                    </Col>
                    <Col span={5}>
                        <Card size="small">
                            <Statistic title="National Avg Score" value={summary?.national_avg_score ?? '—'}
                                suffix={summary?.national_avg_score != null ? '/ 800' : ''}/>
                        </Card>
                    </Col>
                    <Col span={7}>
                        <XCard title="IPC Level Distribution" filename="ipcaf_level_distribution">
                            {levelDonutOpt
                                ? <ReactECharts option={levelDonutOpt} style={{ height:180 }}/>
                                : <Alert type="info" showIcon message="No submitted assessments yet."/>}
                        </XCard>
                    </Col>
                    <Col span={7}>
                        <XCard title="Average Score by Region" filename="ipcaf_region_averages">
                            {regionBarOpt
                                ? <ReactECharts option={regionBarOpt} style={{ height:180 }}/>
                                : <Alert type="info" showIcon message="No submitted assessments yet."/>}
                        </XCard>
                    </Col>
                </Row>

                {/* ── Weakest indicators ── */}
                <XCard title={`Weakest Indicators — Bottom ${bottom15.length || 0} Nationally`}
                    filename="ipcaf_weakest_indicators" style={{ marginBottom:16 }}>
                    {weakestBarOpt
                        ? <ReactECharts option={weakestBarOpt} style={{ height: Math.max(300, bottom15.length * 28) }}/>
                        : <Alert type="info" showIcon message="No submitted assessments yet — indicator rankings will appear once at least one facility has submitted an IPCAF assessment."/>}
                </XCard>

                <Card size="small" title="All 84 Indicators — Ranked Weakest to Strongest" style={{ marginBottom:16 }}>
                    <Table size="small" dataSource={weakest} columns={weakestColumns} rowKey="question_id"
                        pagination={{ pageSize:15 }}/>
                </Card>

                {/* ── Facility scorecard ── */}
                <Card size="small" title="Facility Scorecard"
                    extra={
                        <Select showSearch optionFilterProp="children" allowClear
                            placeholder="Select a facility" style={{ width:260 }}
                            value={facilityId} onChange={setFacilityId}>
                            {facilities.map(f => (
                                <Select.Option key={f.id} value={f.id}>{f.business_name}</Select.Option>
                            ))}
                        </Select>
                    }>
                    {!facilityId ? (
                        <Alert type="info" showIcon message="Select a facility above to view its IPCAF scorecard."/>
                    ) : scorecardLoading ? (
                        <div style={{ textAlign:'center', padding:40 }}><Spin/></div>
                    ) : !scorecard?.latest ? (
                        <Alert type="warning" showIcon
                            message={`${selectedFacility?.business_name || 'This facility'} has no submitted IPCAF assessments yet.`}/>
                    ) : (
                        <Row gutter={16}>
                            <Col span={8}>
                                <div style={{ textAlign:'center', marginBottom:12 }}>
                                    <div style={{ fontSize:11, color:'#888', fontWeight:700 }}>LATEST TOTAL SCORE</div>
                                    <div style={{ fontSize:32, fontWeight:900, color: LEVEL_COLORS[scorecard.latest.ipc_level] }}>
                                        {scorecard.latest.total_score}
                                        <span style={{ fontSize:14, color:'#aaa' }}> / 800</span>
                                    </div>
                                    <Tag color={LEVEL_COLORS[scorecard.latest.ipc_level]}>{scorecard.latest.ipc_level}</Tag>
                                    <div style={{ fontSize:11, color:'#888', marginTop:4 }}>
                                        as of {scorecard.latest.assessment_date}
                                    </div>
                                </div>
                                {radarOpt && <ReactECharts option={radarOpt} style={{ height:280 }}/>}
                            </Col>
                            <Col span={16}>
                                <div style={{ fontSize:12, fontWeight:700, marginBottom:8 }}>
                                    Score Trend {scorecard.history.length < 2 && (
                                        <span style={{ fontWeight:400, color:'#aaa' }}>
                                            (only one submitted assessment so far — trend line will appear after a repeat assessment)
                                        </span>
                                    )}
                                </div>
                                {trendOpt && <ReactECharts option={trendOpt} style={{ height:280 }}/>}
                            </Col>
                        </Row>
                    )}
                </Card>
            </div>
        </div>
    );
}